import type { Field, RuleValidator, ValidationResult } from 'react-querybuilder';
import { SHOW_PROPOSED_COLUMNS } from '../featureFlags';
import type { AudienceFilterKey, AudienceFilters } from '../types';
import { COMMERCE_ATTRIBUTE_OPTIONS, sectionLabel } from './commerceAttributes';
import {
  DEFAULT_PURCHASE_LOOKBACK_DAYS,
  type FilterGroupId,
  isPurchaseLookbackWindow,
  PURCHASE_LOOKBACK_WINDOWS
} from './filters';
import { US_STATES } from './usStates';

/* The one field config for the audience filter builder: labels, domains, operators, editors, and which filter keys each rule writes.
   The react-querybuilder `fields` prop, the column mapper, the mappers, and the read-only rule rows all derive from it.

   Fields with `backendSupport: 'proposed'` are columns proxima-audiences does not filter on.
   They render so the gap stays visible, but the mappers never send them to preview or count. */

export type AudienceFieldName =
  | 'gender'
  | 'age'
  | 'ageBucket'
  | 'income'
  | 'parentalStatus'
  | 'homeowner'
  | 'married'
  | 'residenceType'
  | 'states'
  | 'city'
  | 'metroArea'
  | 'commerceAttributes'
  | 'productCategory'
  | 'productKeywords'
  | 'brandIds'
  | 'brandCategory'
  | 'purchaseLookbackDays'
  | 'orderCount'
  | 'hasDiscountPurchase'
  | 'hasGiftCardPurchase'
  | 'aovPercentile'
  | 'ltvPercentile'
  | 'uniqueIndividualLimit';

export type AudienceOperator = '=' | 'between' | '>=' | '<=' | 'in' | 'notIn';

/**
 * How far the audiences service supports a column.
 *
 * - `supported`: previewed, counted, and created.
 * - `proposed`: not in the service at all; never sent.
 */
export type BackendSupport = 'supported' | 'proposed';

export type FieldOption = { value: string; label: string; detail?: string; group?: string };

type KeysWithValue<Value> = {
  [Key in AudienceFilterKey]: AudienceFilters[Key] extends Value ? Key : never;
}[AudienceFilterKey];

type NumberFilterKey = KeysWithValue<number | null>;
type ListFilterKey = KeysWithValue<string[]>;
type BooleanFilterKey = KeysWithValue<boolean | null>;

/**
 * A column the service filters on.
 *
 * `flatKeys` are the flat filter keys this rule owns; the mappers read and write exactly these.
 */
type ServiceColumn<FlatKeys extends readonly AudienceFilterKey[]> = {
  backendSupport: 'supported';
  flatKeys: FlatKeys;
  proposedKeys?: never;
};

/**
 * A column the service does not filter on yet.
 *
 * - `flatKeys` is always empty, so saved filters never produce the rule.
 * - `proposedKeys` are the keys the value would travel under once the backend supports it.
 */
type ProposedColumn = {
  backendSupport: 'proposed';
  flatKeys: readonly [];
  proposedKeys: readonly string[];
};

type Column<FlatKeys extends readonly AudienceFilterKey[]> =
  | ServiceColumn<FlatKeys>
  | ProposedColumn;

/**
 * A list column with an include side and an exclude side.
 *
 * - `flatKeys` is `[includeKey, excludeKey]`.
 * - The rule's operator picks the side: `in` writes the include key, `notIn` writes the exclude key.
 * - A tree holds at most one rule per side, so one definition can include some values and exclude others.
 */
type NegatableColumn = ServiceColumn<readonly [ListFilterKey, ListFilterKey]> & {
  operators: readonly ['in', 'notIn'];
  defaultOperator: 'in';
  /** Names the exclude side, e.g. on its value editor and its remove button. */
  excludeLabel: string;
};

/* Each editor carries only the props it renders with, and the flat key shape its mapper reads */
type EditorConfig =
  | ({ editor: 'gender' } & Column<readonly ['gender']>)
  | ({ editor: 'range'; prefix?: string } & Column<readonly [NumberFilterKey, NumberFilterKey]>)
  | ({ editor: 'percentRange'; prefix: string } & Column<
      readonly [NumberFilterKey, NumberFilterKey]
    >)
  | ({ editor: 'number' } & Column<readonly [NumberFilterKey, ...AudienceFilterKey[]]>)
  | ({ editor: 'lookback' } & Column<readonly [NumberFilterKey]>)
  | ({ editor: 'states' } & NegatableColumn)
  | ({
      editor: 'options';
      options: readonly FieldOption[];
      placeholder: string;
      tone?: 'include' | 'exclude';
    } & Column<readonly [ListFilterKey]>)
  | ({ editor: 'chips'; placeholder: string; tone?: 'include' | 'exclude' } & Column<
      readonly [ListFilterKey]
    >)
  | ({ editor: 'brandIds' } & NegatableColumn)
  | ({
      editor: 'tristate';
      /** Labels for the true and false choices. */
      choiceLabels: readonly [string, string];
    } & Column<readonly [BooleanFilterKey]>)
  | ({ editor: 'choice'; options: readonly FieldOption[] } & Column<readonly [AudienceFilterKey]>);

export type AudienceFieldEditor = EditorConfig['editor'];

export type AudienceFieldConfig = {
  name: AudienceFieldName;
  label: string;
  domain: FilterGroupId;
  /** Short line under the label in the column mapper and rule row. */
  hint?: string;
  /** What the filter depends on or how it is derived; shown under the rule and in the mapper tooltip. */
  dependsOn?: string;
  /** Counted inside the purchase window, so the dependency note names the window in effect. */
  windowScoped?: boolean;
  operators: readonly AudienceOperator[];
  defaultOperator: AudienceOperator;
  defaultValue: unknown;
  /** Fields that cannot be used together with this one; adding one removes the other. */
  excludes?: readonly AudienceFieldName[];
} & EditorConfig;

export const OPERATOR_LABELS: Record<AudienceOperator, string> = {
  '=': 'is',
  between: 'between',
  '>=': 'at least',
  '<=': 'at most',
  in: 'any of',
  notIn: 'none of'
};

export const SUPPORT_BADGES: Record<
  Exclude<BackendSupport, 'supported'>,
  { label: string; tooltip: string }
> = {
  proposed: {
    label: 'Proposed',
    tooltip: 'Not supported by the backend yet. Preview and create will ignore this filter.'
  }
};

const RANGE_OPERATORS = ['between', '>=', '<='] as const;

const WINDOW_NOTE = 'Counted within the purchase window';

/* Placeholder options until the real catalogs have read endpoints */

const option = (value: string, label: string, group?: string): FieldOption => ({
  value,
  label,
  ...(group ? { group } : {})
});

/* Placeholder: the largest US CBSAs by population, not a verified list */
const METRO_AREA_OPTIONS: FieldOption[] = [
  'New York-Newark-Jersey City',
  'Los Angeles-Long Beach-Anaheim',
  'Chicago-Naperville-Elgin',
  'Dallas-Fort Worth-Arlington',
  'Houston-The Woodlands-Sugar Land',
  'Washington-Arlington-Alexandria',
  'Philadelphia-Camden-Wilmington',
  'Miami-Fort Lauderdale-Pompano Beach',
  'Atlanta-Sandy Springs-Alpharetta',
  'Boston-Cambridge-Newton',
  'Phoenix-Mesa-Chandler',
  'San Francisco-Oakland-Berkeley',
  'Riverside-San Bernardino-Ontario',
  'Detroit-Warren-Dearborn',
  'Seattle-Tacoma-Bellevue',
  'Minneapolis-St. Paul-Bloomington',
  'San Diego-Chula Vista-Carlsbad',
  'Tampa-St. Petersburg-Clearwater',
  'Denver-Aurora-Lakewood',
  'Baltimore-Columbia-Towson'
].map(name => option(name.toLowerCase().replace(/[^a-z]+/g, '_'), name));

const PARENTAL_STATUS_OPTIONS: FieldOption[] = [
  option('any_parent', 'Any parent'),
  option('expectant', 'Expectant'),
  option('new_parent', 'New parent'),
  option('toddlers', 'Parent of toddlers'),
  option('school_age', 'Parent of school-age children')
];

const AGE_BUCKET_OPTIONS: FieldOption[] = [
  '18-24',
  '25-34',
  '35-44',
  '45-54',
  '55-64',
  '65-74',
  '75+'
].map(band => option(band, band));

/* Placeholder: a three-level product taxonomy sample, not the real catalog */
const PRODUCT_CATEGORY_OPTIONS: FieldOption[] = [
  ['Home', 'Kitchen', 'Cookware'],
  ['Home', 'Kitchen', 'Small appliances'],
  ['Home', 'Garden', 'Pest control'],
  ['Home', 'Bedding', 'Mattresses'],
  ['Beauty', 'Skincare', 'Serums'],
  ['Beauty', 'Hair', 'Styling tools'],
  ['Apparel', 'Women', 'Activewear'],
  ['Apparel', 'Men', 'Outerwear'],
  ['Outdoor', 'Camping', 'Tents'],
  ['Outdoor', 'Hunting', 'Optics'],
  ['Pets', 'Dogs', 'Food'],
  ['Baby', 'Feeding', 'Bottles']
].map(([top, middle, leaf]) =>
  option(
    `${top}>${middle}>${leaf}`.toLowerCase().replace(/[^a-z>]+/g, '_'),
    `${middle} › ${leaf}`,
    top
  )
);

/* Placeholder brand category names */
const BRAND_CATEGORY_OPTIONS: FieldOption[] = [
  'Apparel',
  'Beauty',
  'Home',
  'Food & beverage',
  'Health & wellness',
  'Pets',
  'Outdoor',
  'Baby & kids',
  'Electronics',
  'Jewelry & accessories'
].map(name => option(name.toLowerCase().replace(/[^a-z]+/g, '_'), name));

const RESIDENCE_TYPE_OPTIONS: FieldOption[] = [
  option('single_family', 'Single-family'),
  option('multi_family', 'Multi-family'),
  option('other', 'Other')
];

export const STATE_OPTIONS: FieldOption[] = US_STATES.map(state => ({
  value: state.code,
  label: state.name,
  detail: state.code
}));

/* An interest shows its humanized key under its section heading */
export const ATTRIBUTE_OPTIONS: FieldOption[] = COMMERCE_ATTRIBUTE_OPTIONS.map(attribute => ({
  value: attribute.key,
  label: attribute.label,
  group: sectionLabel(attribute.section)
}));

const proposed = (...keys: string[]): ProposedColumn => ({
  backendSupport: 'proposed',
  flatKeys: [],
  proposedKeys: keys
});

const tristate = (trueLabel: string, falseLabel: string) => ({
  editor: 'tristate' as const,
  operators: ['='] as const,
  defaultOperator: '=' as const,
  defaultValue: true,
  choiceLabels: [trueLabel, falseLabel] as const
});

const multi = (options: FieldOption[], placeholder: string, operator: 'in' | 'notIn' = 'in') => ({
  editor: 'options' as const,
  operators: [operator] as const,
  defaultOperator: operator,
  defaultValue: [],
  options,
  placeholder
});

const chips = (placeholder: string) => ({
  editor: 'chips' as const,
  operators: ['in'] as const,
  defaultOperator: 'in' as const,
  defaultValue: [],
  placeholder
});

const percentRange = {
  editor: 'percentRange' as const,
  operators: RANGE_OPERATORS,
  defaultOperator: 'between' as const,
  defaultValue: ['', ''],
  prefix: '%'
};

export const AUDIENCE_FIELDS: readonly AudienceFieldConfig[] = [
  // Demographics
  {
    name: 'gender',
    label: 'Gender',
    domain: 'demographics',
    backendSupport: 'supported',
    editor: 'gender',
    operators: ['='],
    defaultOperator: '=',
    defaultValue: 'any',
    flatKeys: ['gender']
  },
  {
    name: 'age',
    label: 'Age',
    domain: 'demographics',
    backendSupport: 'supported',
    hint: 'Years',
    excludes: ['ageBucket'],
    editor: 'range',
    operators: RANGE_OPERATORS,
    defaultOperator: 'between',
    defaultValue: ['', ''],
    flatKeys: ['ageMin', 'ageMax']
  },
  {
    name: 'ageBucket',
    label: 'Banded age',
    domain: 'demographics',
    ...proposed('ageBucket'),
    hint: 'Age bands',
    dependsOn: 'Predicted from first name and zip; low confidence.',
    excludes: ['age'],
    ...multi(AGE_BUCKET_OPTIONS, 'Pick age bands…')
  },
  {
    name: 'income',
    label: 'Income',
    domain: 'demographics',
    backendSupport: 'supported',
    editor: 'range',
    operators: RANGE_OPERATORS,
    defaultOperator: '>=',
    defaultValue: '',
    flatKeys: ['incomeMin', 'incomeMax'],
    prefix: '$'
  },
  {
    name: 'parentalStatus',
    label: 'Parental status',
    domain: 'demographics',
    ...proposed('parentalStatus'),
    ...multi(PARENTAL_STATUS_OPTIONS, 'Pick a parental status…')
  },
  {
    name: 'homeowner',
    label: 'Homeowner',
    domain: 'demographics',
    ...proposed('homeowner'),
    ...tristate('Owns a home', 'Does not own')
  },
  {
    name: 'married',
    label: 'Married',
    domain: 'demographics',
    ...proposed('married'),
    ...tristate('Married', 'Not married')
  },
  {
    name: 'residenceType',
    label: 'Residence type',
    domain: 'demographics',
    ...proposed('residenceType'),
    editor: 'choice',
    operators: ['='],
    defaultOperator: '=',
    defaultValue: 'single_family',
    options: RESIDENCE_TYPE_OPTIONS
  },
  // Geography
  {
    name: 'states',
    label: 'States',
    domain: 'geography',
    backendSupport: 'supported',
    editor: 'states',
    operators: ['in', 'notIn'],
    defaultOperator: 'in',
    defaultValue: [],
    flatKeys: ['includedStates', 'excludedStates'],
    excludeLabel: 'Not in states'
  },
  {
    name: 'city',
    label: 'City',
    domain: 'geography',
    ...proposed('city'),
    ...chips('Type a city and press Enter')
  },
  {
    name: 'metroArea',
    label: 'Metro area',
    domain: 'geography',
    ...proposed('metroArea'),
    hint: 'CBSA',
    ...multi(METRO_AREA_OPTIONS, 'Search metro areas…')
  },
  // Interests
  {
    name: 'commerceAttributes',
    label: 'Interests',
    domain: 'interests',
    backendSupport: 'supported',
    hint: 'Matches any one',
    ...multi(ATTRIBUTE_OPTIONS, 'Search interests, e.g. dog owner…'),
    flatKeys: ['commerceAttributes']
  },
  {
    name: 'productCategory',
    label: 'Product category',
    domain: 'interests',
    ...proposed('productCategory'),
    ...multi(PRODUCT_CATEGORY_OPTIONS, 'Search product categories…')
  },
  {
    name: 'productKeywords',
    label: 'Product keywords',
    domain: 'interests',
    ...proposed('productKeywords'),
    ...chips('Type a keyword and press Enter')
  },
  // Brand affinity
  {
    name: 'brandIds',
    label: 'Bought from brands',
    domain: 'brands',
    backendSupport: 'supported',
    editor: 'brandIds',
    operators: ['in', 'notIn'],
    defaultOperator: 'in',
    defaultValue: [],
    flatKeys: ['includedBrandIds', 'excludedBrandIds'],
    excludeLabel: 'Never bought from brands'
  },
  {
    name: 'brandCategory',
    label: 'Brand category',
    domain: 'brands',
    ...proposed('brandCategory'),
    ...multi(BRAND_CATEGORY_OPTIONS, 'Pick brand categories…')
  },
  // Behaviors
  {
    name: 'purchaseLookbackDays',
    label: 'Purchase window',
    domain: 'behaviors',
    backendSupport: 'supported',
    editor: 'lookback',
    operators: ['='],
    defaultOperator: '=',
    defaultValue: DEFAULT_PURCHASE_LOOKBACK_DAYS,
    flatKeys: ['purchaseLookbackDays']
  },
  {
    name: 'orderCount',
    label: 'Order count',
    hint: 'Across all brands',
    domain: 'behaviors',
    backendSupport: 'supported',
    editor: 'range',
    operators: RANGE_OPERATORS,
    defaultOperator: '>=',
    defaultValue: '',
    flatKeys: ['orderCountMin', 'orderCountMax']
  },
  {
    name: 'hasDiscountPurchase',
    label: 'Discount purchase',
    domain: 'behaviors',
    backendSupport: 'supported',
    ...tristate('Has used one', 'Never used one'),
    flatKeys: ['hasDiscountPurchase']
  },
  {
    name: 'hasGiftCardPurchase',
    label: 'Gift card purchase',
    domain: 'behaviors',
    ...proposed('hasGiftCardPurchase'),
    dependsOn: WINDOW_NOTE,
    windowScoped: true,
    ...tristate('Has bought one', 'Never bought one')
  },
  {
    name: 'aovPercentile',
    label: 'AOV percentile',
    domain: 'behaviors',
    ...proposed('aovPercentileMin', 'aovPercentileMax'),
    hint: 'Average order value rank',
    dependsOn: WINDOW_NOTE,
    windowScoped: true,
    ...percentRange
  },
  {
    name: 'ltvPercentile',
    label: 'LTV percentile',
    domain: 'behaviors',
    ...proposed('ltvPercentileMin', 'ltvPercentileMax'),
    hint: 'Lifetime value rank',
    dependsOn: WINDOW_NOTE,
    windowScoped: true,
    ...percentRange
  },
  // Size cap
  {
    name: 'uniqueIndividualLimit',
    label: 'Size cap',
    domain: 'size',
    backendSupport: 'supported',
    hint: 'Top N by LTV',
    dependsOn: 'Ranked by order count, then LTV, then recency.',
    editor: 'number',
    operators: ['<='],
    defaultOperator: '<=',
    defaultValue: '',
    flatKeys: ['uniqueIndividualLimit', 'rankColumn']
  }
];

const FIELD_BY_NAME = new Map(AUDIENCE_FIELDS.map(field => [field.name, field]));

export const audienceField = (name: string): AudienceFieldConfig | undefined =>
  FIELD_BY_NAME.get(name as AudienceFieldName);

export const isProposedField = (field: AudienceFieldConfig | undefined): boolean =>
  field?.backendSupport === 'proposed';

/** Whether the create page lists the field in Add Filters and lets it be added; proposed columns only when `SHOW_PROPOSED_COLUMNS` is on. */
export const isOfferedField = (field: AudienceFieldConfig): boolean =>
  SHOW_PROPOSED_COLUMNS || !isProposedField(field);

/* The rule that owns a flat key, e.g. ageMax -> age */
export const fieldForFlatKey = (key: AudienceFilterKey): AudienceFieldConfig | undefined =>
  AUDIENCE_FIELDS.find(field => field.flatKeys.some(flatKey => flatKey === key));

export type NegatableField = AudienceFieldConfig & { editor: 'states' | 'brandIds' };

/** Whether the field has an include side and an exclude side, picked by the Not switch on its row. */
export const isNegatableField = (field: AudienceFieldConfig | undefined): field is NegatableField =>
  field?.editor === 'states' || field?.editor === 'brandIds';

/** The operator that selects a negatable field's exclude side. */
export const NEGATED_OPERATOR = 'notIn' satisfies AudienceOperator;

/**
 * The slot a rule occupies in the tree; a tree holds at most one rule per slot.
 *
 * - A negatable field has two slots, one per side, e.g. `states:in` and `states:notIn`.
 * - Every other field has one slot, its name.
 */
export const ruleSlot = (field: AudienceFieldConfig, operator: string): string =>
  isNegatableField(field) ? `${field.name}:${operator}` : field.name;

/** The name of one rule's side: the exclude label for a negatable field's Not rule, the field label otherwise. */
export const ruleLabel = (field: AudienceFieldConfig, operator: string): string =>
  isNegatableField(field) && operator === NEGATED_OPERATOR ? field.excludeLabel : field.label;

/** The flat keys one rule writes: one side's key for a negatable field, every flat key otherwise. */
export const ruleFlatKeys = (
  field: AudienceFieldConfig,
  operator: string
): readonly AudienceFilterKey[] => {
  if (!isNegatableField(field)) return field.flatKeys;
  const [includeKey, excludeKey] = field.flatKeys;
  return [operator === NEGATED_OPERATOR ? excludeKey : includeKey];
};

/** The operator of the rule that writes a flat key, e.g. excludedStates -> notIn. */
export const operatorForFlatKey = (
  field: AudienceFieldConfig,
  key: AudienceFilterKey
): AudienceOperator =>
  isNegatableField(field) && field.flatKeys[1] === key ? NEGATED_OPERATOR : field.defaultOperator;

/**
 * The dependency note for a field, with the window in effect for window-scoped fields.
 *
 * `lookbackDays` is the purchase window rule's value, or null when there is no such rule.
 */
export const dependsOnHint = (
  field: AudienceFieldConfig | undefined,
  lookbackDays: number | null
): string | null => {
  if (!field?.dependsOn) return null;
  if (!field.windowScoped) return field.dependsOn;
  return lookbackDays == null
    ? `${field.dependsOn} (${DEFAULT_PURCHASE_LOOKBACK_DAYS} days, the default, since no window is set).`
    : `${field.dependsOn} (${lookbackDays} days).`;
};

/**
 * The purchase windows the builder shows: `PURCHASE_LOOKBACK_WINDOWS`, plus `current` when it is outside them.
 *
 * A legacy definition can carry such a window, and it stays visible so the rule reads as saved.
 */
export const lookbackOptionsFor = (current: number | null): number[] => {
  const options: number[] = [...PURCHASE_LOOKBACK_WINDOWS];
  if (current != null && !isPurchaseLookbackWindow(current)) options.push(current);
  return options.sort((a, b) => a - b);
};

/* Validators */

const invalid = (reason: string): ValidationResult => ({ valid: false, reasons: [reason] });

/** `''`, null, and non-numbers are "no value"; everything else must be a finite number. */
export const toNumber = (value: unknown): number | undefined => {
  if (value === '' || value == null || typeof value === 'boolean') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export const rangeBounds = (value: unknown): [unknown, unknown] => {
  if (Array.isArray(value)) return [value[0], value[1]];
  const [low, high] = String(value ?? '').split(',');
  return [low, high];
};

const rangeValidatorWithin =
  (min: number, max: number): RuleValidator =>
  rule => {
    const outOfBounds = (bound: number) => bound < min || bound > max;
    if (rule.operator === 'between') {
      const [low, high] = rangeBounds(rule.value).map(toNumber);
      if (low === undefined || high === undefined) return invalid('Enter both bounds');
      if (outOfBounds(low) || outOfBounds(high)) return invalid('Out of range');
      if (low > high) return invalid('Max must be at least the min');
      return true;
    }
    const value = toNumber(rule.value);
    if (value === undefined) return invalid('Enter a number');
    return outOfBounds(value) ? invalid('Out of range') : true;
  };

const positiveValidator: RuleValidator = rule => {
  const value = toNumber(rule.value);
  if (value === undefined) return invalid('Enter a number');
  return value >= 1 ? true : invalid('Must be 1 or more');
};

const listValidator: RuleValidator = rule =>
  Array.isArray(rule.value) && rule.value.length > 0 ? true : invalid('Pick at least one');

const requiredValidator: RuleValidator = rule =>
  rule.value === '' || rule.value == null ? invalid('Pick a value') : true;

const VALIDATORS: Record<AudienceFieldEditor, RuleValidator> = {
  gender: requiredValidator,
  range: rangeValidatorWithin(0, Number.MAX_SAFE_INTEGER),
  percentRange: rangeValidatorWithin(0, 1),
  number: positiveValidator,
  states: listValidator,
  options: listValidator,
  chips: listValidator,
  brandIds: listValidator,
  lookback: positiveValidator,
  tristate: requiredValidator,
  choice: requiredValidator
};

/** The `fields` prop for react-querybuilder, derived from `AUDIENCE_FIELDS`. */
export const QUERY_BUILDER_FIELDS: Field[] = AUDIENCE_FIELDS.map(field => ({
  name: field.name,
  label: field.label,
  operators: field.operators.map(operator => ({
    name: operator,
    label: OPERATOR_LABELS[operator]
  })),
  defaultOperator: field.defaultOperator,
  defaultValue: field.defaultValue,
  inputType: ['range', 'percentRange', 'number'].includes(field.editor) ? 'number' : 'text',
  validator: VALIDATORS[field.editor]
}));

/* A flat AND list: no groups, no OR, no NOT */
export const FLAT_AND_COMBINATORS = [{ name: 'and', label: 'AND' }];
